import { useEffect, useRef, useState } from 'react'
import { useAppStore, useVisibilityStore } from '@/shared/store'
import { getErrorMessage, isStreamIQ, isToya } from '@/shared/lib'
import { defaultProfile, LAST_ACTUAL_PROFILE } from '@/shared/constants'
import { Profile } from '@/shared/store/useAppStore'
import { useLoginAndProfileRepository } from '@/features/repositories/useLoginAndProfileRepository'
import {
    getFromLocalStorage,
    saveToLocalStorage,
} from '@/shared/localStorage/localStorageRepository'
import useKeyDown from '@/shared/hooks/useKeyDown'
import { getApiService } from '@/api/getApiService'
import { fetchBootstraps } from '@/clients/streamiq/shared/lib'

export const useLogInMenuHook = () => {
    const [isResetPassword, setIsResetPassword] = useState(false)
    const [shouldShowQRLogin, setShouldShowQRLogin] = useState(false)
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [inputErrors, setInputErrors] = useState({
        usernameError: '',
        passwordError: '',
        dataValidationError: '',
    })
    const loginApi = getApiService()
    const usernameRef = useRef(username)
    const passwordRef = useRef(password)

    useEffect(() => {
        usernameRef.current = username
        passwordRef.current = password
    }, [username, password])

    const { setProfiles, setActualProfile, setEmail, setIsUserLoggedIn } =
        useAppStore()

    const { setHomeContentVisible } = useVisibilityStore()
    const { fetchUserProfiles } = useLoginAndProfileRepository()

    const checkUsernameChange = (newUsername: string) => {
        setUsername(newUsername)

        setInputErrors((prev) => ({
            ...prev,
            dataValidationError: '',
        }))
        if (!newUsername)
            setInputErrors((prev) => ({
                ...prev,
                usernameError: 'usernameFieldCannotBeEmpty',
            }))
        else {
            setInputErrors((prev) => ({
                ...prev,
                usernameError: '',
            }))
        }
    }

    const checkPasswordChange = (newPassword: string) => {
        setPassword(newPassword)

        setInputErrors((prev) => ({
            ...prev,
            dataValidationError: '',
        }))

        if (!newPassword.length)
            setInputErrors((prev) => ({
                ...prev,
                passwordError: 'passwordFieldCannotBeEmpty',
            }))
        else
            setInputErrors((prev) => ({
                ...prev,
                passwordError: '',
            }))
    }

    const logIn = async () => {
        const currentUsername = usernameRef.current
        const currentPassword = passwordRef.current

        setEmail(username)

        if (!currentUsername || !currentPassword) {
            setInputErrors((prev) => ({
                ...prev,
                usernameError: !currentUsername
                    ? 'usernameFieldCannotBeEmpty'
                    : '',
                passwordError: !currentPassword
                    ? 'passwordFieldCannotBeEmpty'
                    : '',
            }))
            return
        }

        try {
            await fetchBootstraps()

            const token = await loginApi.signIn(
                currentUsername,
                currentPassword
            )

            if (!token) {
                setInputErrors((prev) => ({
                    ...prev,
                    dataValidationError: 'loginDataIsInvalid',
                }))
                return
            }

            setInputErrors({
                usernameError: '',
                passwordError: '',
                dataValidationError: '',
            })

            setIsUserLoggedIn(true)
            setHomeContentVisible()

            const profiles = (await fetchUserProfiles()) as Profile[]

            if (isToya()) {
                setProfiles(profiles?.length ? profiles : [defaultProfile])
            }
            if (isStreamIQ() && profiles.length) {
                setProfiles(profiles)
            } else if (isStreamIQ() && !profiles.length) {
                setProfiles([])
            }

            const actualProfile = getFromLocalStorage(LAST_ACTUAL_PROFILE)

            if (actualProfile && profiles?.length) {
                const profile = profiles.find(
                    (profile) => profile.id === actualProfile
                )
                if (profile) {
                    setActualProfile(profile)
                }
            } else if (profiles?.length) {
                setActualProfile(profiles[0])
                saveToLocalStorage(LAST_ACTUAL_PROFILE, `${profiles[0]?.id}`)
            }
        } catch (error) {
            console.log(error)
            getErrorMessage('logIn', error)
        }
    }

    const isError = Boolean(
        inputErrors.usernameError || inputErrors.passwordError
    )

    useKeyDown(logIn)

    const showQRCodeHandler = () => setShouldShowQRLogin((prev) => !prev)

    const resetPassword = (email: string) => loginApi.handleResetRequest(email)

    const handleBackToLogin = () => {
        setIsResetPassword(false)
    }

    return {
        username,
        password,
        inputErrors,
        checkUsernameChange,
        checkPasswordChange,
        logIn,
        isError,
        shouldShowQRLogin,
        setShouldShowQRLogin,
        showQRCodeHandler,
        resetPassword,
        isResetPassword,
        setIsResetPassword,
        handleBackToLogin,
    }
}
